import RefreshIcon from '@mui/icons-material/Refresh';
import { Alert, Button } from '@mui/material';
import React from 'react';
import './index.scss';

interface ErrorMessageProps {
  message: string;
  /**
   * Offer a retry.
   *
   * Most failures behind this component are a fetch that did not come back, and without this the
   * only recovery on offer is a full page reload — which the user has to think of themselves. Pass
   * the same loader the page runs on mount.
   */
  onRetry?: () => void;
  /** Label for the retry action, when "Retry" is not specific enough. */
  retryLabel?: string;
  /**
   * This error IS the page, rather than sitting inside one.
   *
   * A page-level error replaces the whole layout, so it also loses the gutter and width cap that
   * layout was providing -- which is how "Job application not found." ended up spanning the full
   * viewport, flush to both edges, while every other page on the console stops at
   * `--page-max-width`. Inline uses (inside a panel that already pads) must NOT set this.
   */
  page?: boolean;
}

/**
 * The shared error surface.
 *
 * Nine pages rendered errors as an ad-hoc `<Typography color="error">` or a bare `<Alert>` instead
 * of using this, which meant nine slightly different treatments and, on some of them, no error
 * surface at all — `Settings/JobMetadataPage` sent failures only to a toast, so a page that failed
 * to load simply looked empty. A toast is a notification; it is not an error state, and it is gone
 * in four seconds.
 */
const ErrorMessage: React.FC<ErrorMessageProps> = ({ message, onRetry, retryLabel = 'Retry', page = false }) => (
  <Alert
    severity="error"
    className={`cc-error${page ? ' cc-error--page' : ''}`}
    action={
      onRetry ? (
        <Button color="inherit" size="small" startIcon={<RefreshIcon />} onClick={onRetry}>
          {retryLabel}
        </Button>
      ) : undefined
    }
  >
    {message}
  </Alert>
);

export default ErrorMessage;
